import React, { useState } from "react";
import { Card, Typography, Row, Col, Layout, Select } from "antd";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const { Title, Paragraph, Text } = Typography;
const { Content, Footer } = Layout;

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const StyledCard = styled(Card)`
  margin-bottom: 2rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const LanguageSwitch = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
  align-items: center;
`;

const Logo = styled.img`
  height: 60px;
  margin-bottom: 1rem;
`;

const StyledFooter = styled(Footer)`
  text-align: center;
  background: #f0f2f5;
  padding: 1.5rem;
  font-size: 12px;
`;

export const PrivacyPolicy: React.FC = () => {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);

  const changeLanguage = (newLanguage: string) => {
    i18n.changeLanguage(newLanguage);
    setLanguage(newLanguage);
  };

  const privacyContentEn = (
    <>
      <Title level={1}>Privacy Policy</Title>
      <Paragraph>
        <Text strong>Last Updated: {new Date().toLocaleDateString()}</Text>
      </Paragraph>

      <Title level={2}>1. Introduction</Title>
      <Paragraph>
        E-Freight Forwarding ("we", "our", or "us") is committed to protecting
        your privacy. This Privacy Policy explains how we collect, use,
        disclose, and safeguard your information when you use our freight
        forwarding and logistics platform.
      </Paragraph>
      <Paragraph>
        We provide digital logistics and freight forwarding services, enabling
        businesses to manage their shipping operations efficiently. This policy
        applies to our website, (mobile) application, and all related services.
      </Paragraph>

      <Title level={2}>2. Information We Collect</Title>
      <Paragraph>
        <Text strong>Personal Information:</Text> We collect information that
        can be used to identify you, such as your name, email address, phone
        number, company information, shipping addresses, and billing details.
      </Paragraph>
      <Paragraph>
        <Text strong>Business Information:</Text> We collect information about
        your business, including company name, registration details, VAT number,
        EORI number, and other relevant business identifiers.
      </Paragraph>
      <Paragraph>
        <Text strong>Shipping Data:</Text> Information about your shipments,
        including origin, destination, product details, customs information, and
        tracking data.
      </Paragraph>
      <Paragraph>
        <Text strong>Usage Data:</Text> Information about how you use our
        platform, including logs, device information, IP addresses, browser
        type, pages visited, and interactions with our service.
      </Paragraph>

      <Title level={2}>3. How We Collect Information</Title>
      <Paragraph>
        <Text strong>Direct Collection:</Text> Information you provide when you
        register, create a profile, book shipments, or communicate with us.
      </Paragraph>
      <Paragraph>
        <Text strong>Automated Collection:</Text> We use cookies and similar
        technologies to collect information about your interactions with our
        platform. We use analytics services to track and analyze usage patterns.
      </Paragraph>
      <Paragraph>
        <Text strong>Third-Party Sources:</Text> We may receive information from
        business partners, shipping carriers, and other third parties involved
        in fulfilling your shipping needs.
      </Paragraph>

      <Title level={2}>4. How We Use Your Information</Title>
      <Paragraph>We use your information for the following purposes:</Paragraph>
      <ul>
        <li>
          Provide and manage our logistics and freight forwarding services
        </li>
        <li>Process and track shipments</li>
        <li>
          Facilitate customs clearance and compliance with international
          shipping regulations
        </li>
        <li>Generate shipping documents and labels</li>
        <li>
          Send transactional emails and notifications about your shipments
        </li>
        <li>Authenticate your identity and maintain account security</li>
        <li>Improve our platform and develop new features</li>
        <li>Analyze usage patterns and optimize user experience</li>
        <li>Comply with legal obligations and enforce our terms of service</li>
      </ul>

      <Title level={2}>5. Service Providers We Use</Title>
      <Paragraph>
        We use several third-party service providers to help operate our
        platform and deliver our services:
      </Paragraph>
      <ul>
        <li>
          <Text strong>Database and Authentication:</Text> We use Supabase to
          securely store and manage your account information and data.
        </li>
        <li>
          <Text strong>Data Processing Tools:</Text> We use Retool to process
          and manage your shipping data.
        </li>
        <li>
          <Text strong>Analytics:</Text> We use PostHog to analyze platform
          usage and improve our services.
        </li>
        <li>
          <Text strong>Hosting and Infrastructure:</Text> Our platform is hosted
          on Vercel's infrastructure.
        </li>
        <li>
          <Text strong>Email Communication:</Text> We use a third-party email
          service provider to send transactional emails about your shipments and
          account.
        </li>
        <li>
          <Text strong>Workflow Automation:</Text> We use n8n to automate
          certain processes related to shipping and logistics.
        </li>
      </ul>
      <Paragraph>
        Each of these service providers has their own privacy policies and
        security practices. We carefully select these providers and share only
        the information necessary to deliver the specific service.
      </Paragraph>

      <Title level={2}>6. Data Retention</Title>
      <Paragraph>
        We retain your personal information for as long as necessary to provide
        you with our services and fulfill the purposes outlined in this Privacy
        Policy. We also retain information as required by law, to resolve
        disputes, and to enforce our agreements.
      </Paragraph>
      <Paragraph>
        Shipping records are typically kept for at least 7 years to comply with
        various international shipping, customs, and tax regulations.
      </Paragraph>

      <Title level={2}>7. Data Security</Title>
      <Paragraph>
        We implement appropriate technical and organizational measures to
        protect your personal information from unauthorized access, loss,
        misuse, or alteration. These measures include encryption, access
        controls, and regular security assessments.
      </Paragraph>
      <Paragraph>
        While we strive to use commercially acceptable means to protect your
        personal information, no method of transmission over the Internet or
        electronic storage is 100% secure, and we cannot guarantee absolute
        security.
      </Paragraph>

      <Title level={2}>8. International Data Transfers</Title>
      <Paragraph>
        Your information may be transferred to and processed in countries other
        than the one in which you reside. These countries may have different
        data protection laws than your country of residence.
      </Paragraph>
      <Paragraph>
        When we transfer your information internationally, we take steps to
        ensure that appropriate safeguards are in place to protect your
        information and to ensure that it is treated in accordance with this
        Privacy Policy.
      </Paragraph>

      <Title level={2}>9. Your Privacy Rights</Title>
      <Paragraph>
        Depending on your location, you may have certain rights regarding your
        personal information:
      </Paragraph>
      <ul>
        <li>
          <Text strong>Access:</Text> You can request a copy of the personal
          information we hold about you.
        </li>
        <li>
          <Text strong>Correction:</Text> You can request that we correct
          inaccurate or incomplete information.
        </li>
        <li>
          <Text strong>Deletion:</Text> You can request that we delete your
          personal information under certain circumstances.
        </li>
        <li>
          <Text strong>Restriction:</Text> You can request that we restrict the
          processing of your information.
        </li>
        <li>
          <Text strong>Data Portability:</Text> You can request a copy of your
          information in a structured, commonly used, and machine-readable
          format.
        </li>
        <li>
          <Text strong>Objection:</Text> You can object to our processing of
          your personal information under certain circumstances.
        </li>
      </ul>
      <Paragraph>
        To exercise these rights, please contact us using the contact
        information provided at the end of this policy.
      </Paragraph>

      <Title level={2}>10. Cookies and Similar Technologies</Title>
      <Paragraph>
        We use cookies and similar tracking technologies to track activity on
        our platform and hold certain information. Cookies are files with a
        small amount of data that may include an anonymous unique identifier.
      </Paragraph>
      <Paragraph>We use the following types of cookies:</Paragraph>
      <ul>
        <li>
          <Text strong>Essential cookies:</Text> Necessary for the platform to
          function properly
        </li>
        <li>
          <Text strong>Functionality cookies:</Text> Remember your preferences
          and settings
        </li>
        <li>
          <Text strong>Analytics cookies:</Text> Help us understand how you use
          our platform
        </li>
        <li>
          <Text strong>Performance cookies:</Text> Help us optimize the
          performance of our platform
        </li>
      </ul>
      <Paragraph>
        You can instruct your browser to refuse all cookies or to indicate when
        a cookie is being sent. However, if you do not accept cookies, some
        parts of our platform may not function properly.
      </Paragraph>

      <Title level={2}>11. Children's Privacy</Title>
      <Paragraph>
        Our services are not directed to individuals under the age of 18. We do
        not knowingly collect personal information from children. If you are a
        parent or guardian and believe your child has provided us with personal
        information, please contact us.
      </Paragraph>

      <Title level={2}>12. Changes to This Privacy Policy</Title>
      <Paragraph>
        We may update our Privacy Policy from time to time. We will notify you
        of any changes by posting the new Privacy Policy on this page and
        updating the "Last Updated" date. You are advised to review this Privacy
        Policy periodically for any changes.
      </Paragraph>

      <Title level={2}>13. Contact Us</Title>
      <Paragraph>
        If you have any questions about this Privacy Policy, please contact us
        at:
      </Paragraph>
      <Paragraph>
        Email: info@efreightforwarding.com
        <br />
        Address: Adres: Ir D S Tuijnmanweg 1A 4131PN, Vianen
      </Paragraph>
    </>
  );

  const privacyContentNl = (
    <>
      <Title level={1}>Privacybeleid</Title>
      <Paragraph>
        <Text strong>Laatst bijgewerkt: {new Date().toLocaleDateString()}</Text>
      </Paragraph>

      <Title level={2}>1. Inleiding</Title>
      <Paragraph>
        E-Freight Forwarding ("wij", "onze" of "ons") zet zich in voor de
        bescherming van uw privacy. Dit Privacybeleid legt uit hoe wij uw
        informatie verzamelen, gebruiken, delen en beschermen wanneer u gebruik
        maakt van ons expeditie- en logistiek platform.
      </Paragraph>
      <Paragraph>
        Wij bieden digitale logistieke en expeditiediensten aan, waarmee
        bedrijven hun verzendingen efficiënt kunnen beheren. Dit beleid is van
        toepassing op onze website, (mobiele) applicatie en alle gerelateerde
        diensten.
      </Paragraph>

      <Title level={2}>2. Informatie die wij verzamelen</Title>
      <Paragraph>
        <Text strong>Persoonlijke informatie:</Text> Wij verzamelen informatie
        die kan worden gebruikt om u te identificeren, zoals uw naam,
        e-mailadres, telefoonnummer, bedrijfsgegevens, verzendadressen en
        factureringsgegevens.
      </Paragraph>
      <Paragraph>
        <Text strong>Bedrijfsinformatie:</Text> Wij verzamelen informatie over
        uw bedrijf, waaronder bedrijfsnaam, registratiegegevens, BTW-nummer,
        EORI-nummer en andere relevante bedrijfsidentificatiegegevens.
      </Paragraph>
      <Paragraph>
        <Text strong>Verzendgegevens:</Text> Informatie over uw zendingen,
        waaronder herkomst, bestemming, productdetails, douane-informatie en
        trackinggegevens.
      </Paragraph>
      <Paragraph>
        <Text strong>Gebruiksgegevens:</Text> Informatie over hoe u ons platform
        gebruikt, waaronder logs, apparaat- informatie, IP-adressen,
        browsertype, bezochte pagina's en interacties met onze dienst.
      </Paragraph>

      <Title level={2}>3. Hoe wij informatie verzamelen</Title>
      <Paragraph>
        <Text strong>Directe verzameling:</Text> Informatie die u verstrekt bij
        registratie, het maken van een profiel, het boeken van zendingen of
        communicatie met ons.
      </Paragraph>
      <Paragraph>
        <Text strong>Geautomatiseerde verzameling:</Text> Wij gebruiken cookies
        en vergelijkbare technologieën om informatie te verzamelen over uw
        interacties met ons platform. We gebruiken analyseservices om
        gebruikspatronen te volgen en te analyseren.
      </Paragraph>
      <Paragraph>
        <Text strong>Derden bronnen:</Text> Wij kunnen informatie ontvangen van
        zakenpartners, vervoerders en andere derden die betrokken zijn bij het
        uitvoeren van uw verzendingen.
      </Paragraph>

      <Title level={2}>4. Hoe wij uw informatie gebruiken</Title>
      <Paragraph>
        Wij gebruiken uw informatie voor de volgende doeleinden:
      </Paragraph>
      <ul>
        <li>Het leveren en beheren van onze logistieke en expeditiediensten</li>
        <li>Het verwerken en volgen van zendingen</li>
        <li>
          Het faciliteren van douaneafhandeling en naleving van internationale
          verzendregelgeving
        </li>
        <li>Het genereren van verzenddocumenten en labels</li>
        <li>
          Het verzenden van transactionele e-mails en notificaties over uw
          zendingen
        </li>
        <li>
          Het authenticeren van uw identiteit en het handhaven van
          accountbeveiliging
        </li>
        <li>
          Het verbeteren van ons platform en het ontwikkelen van nieuwe functies
        </li>
        <li>
          Het analyseren van gebruikspatronen en het optimaliseren van de
          gebruikerservaring
        </li>
        <li>
          Het naleven van wettelijke verplichtingen en het handhaven van onze
          gebruiksvoorwaarden
        </li>
      </ul>

      <Title level={2}>5. Dienstverleners die wij gebruiken</Title>
      <Paragraph>
        Wij maken gebruik van verschillende externe dienstverleners om ons
        platform te laten functioneren en onze diensten te leveren:
      </Paragraph>
      <ul>
        <li>
          <Text strong>Database en Authenticatie:</Text> Wij gebruiken Supabase
          om uw accountgegevens en data veilig op te slaan en te beheren.
        </li>
        <li>
          <Text strong>Gegevensverwerkingstools:</Text> Wij gebruiken Retool om
          uw verzendgegevens te verwerken en te beheren.
        </li>
        <li>
          <Text strong>Analytics:</Text> Wij gebruiken PostHog om
          platformgebruik te analyseren en onze diensten te verbeteren.
        </li>
        <li>
          <Text strong>Hosting en Infrastructuur:</Text> Ons platform wordt
          gehost op de infrastructuur van Vercel.
        </li>
        <li>
          <Text strong>E-mailcommunicatie:</Text> Wij gebruiken een externe
          e-mailserviceprovider om transactionele e-mails over uw zendingen en
          account te versturen.
        </li>
        <li>
          <Text strong>Werkstroomautomatisering:</Text> Wij gebruiken n8n om
          bepaalde processen met betrekking tot verzending en logistiek te
          automatiseren.
        </li>
      </ul>
      <Paragraph>
        Elk van deze dienstverleners heeft zijn eigen privacybeleid en
        beveiligingspraktijken. Wij selecteren deze aanbieders zorgvuldig en
        delen alleen de informatie die nodig is om de specifieke dienst te
        leveren.
      </Paragraph>

      <Title level={2}>6. Gegevensbewaring</Title>
      <Paragraph>
        Wij bewaren uw persoonlijke informatie zo lang als nodig is om u onze
        diensten te leveren en de doeleinden te vervullen die in dit
        Privacybeleid zijn beschreven. We bewaren informatie ook zoals vereist
        door de wet, om geschillen op te lossen en om onze overeenkomsten te
        handhaven.
      </Paragraph>
      <Paragraph>
        Verzendgegevens worden doorgaans minimaal 7 jaar bewaard om te voldoen
        aan verschillende internationale verzend-, douane- en
        belastingvoorschriften.
      </Paragraph>

      <Title level={2}>7. Gegevensbeveiliging</Title>
      <Paragraph>
        Wij implementeren passende technische en organisatorische maatregelen om
        uw persoonlijke informatie te beschermen tegen ongeautoriseerde toegang,
        verlies, misbruik of wijziging. Deze maatregelen omvatten versleuteling,
        toegangscontroles en regelmatige veiligheidsbeoordelingen.
      </Paragraph>
      <Paragraph>
        Hoewel wij streven naar het gebruik van commercieel aanvaardbare
        middelen om uw persoonlijke informatie te beschermen, is geen enkele
        methode van verzending via het internet of elektronische opslag 100%
        veilig, en kunnen wij absolute veiligheid niet garanderen.
      </Paragraph>

      <Title level={2}>8. Internationale gegevensoverdrachten</Title>
      <Paragraph>
        Uw informatie kan worden overgedragen naar en verwerkt in landen buiten
        het land waar u woont. Deze landen kunnen andere
        gegevensbeschermingswetten hebben dan uw land van verblijf.
      </Paragraph>
      <Paragraph>
        Wanneer wij uw informatie internationaal overdragen, nemen wij
        maatregelen om ervoor te zorgen dat er passende waarborgen zijn om uw
        informatie te beschermen en ervoor te zorgen dat deze wordt behandeld in
        overeenstemming met dit Privacybeleid.
      </Paragraph>

      <Title level={2}>9. Uw privacyrechten</Title>
      <Paragraph>
        Afhankelijk van uw locatie heeft u mogelijk bepaalde rechten met
        betrekking tot uw persoonlijke informatie:
      </Paragraph>
      <ul>
        <li>
          <Text strong>Toegang:</Text> U kunt een kopie opvragen van de
          persoonlijke informatie die wij over u hebben.
        </li>
        <li>
          <Text strong>Correctie:</Text> U kunt verzoeken dat wij onjuiste of
          onvolledige informatie corrigeren.
        </li>
        <li>
          <Text strong>Verwijdering:</Text> U kunt verzoeken dat wij uw
          persoonlijke informatie onder bepaalde omstandigheden verwijderen.
        </li>
        <li>
          <Text strong>Beperking:</Text> U kunt verzoeken dat wij de verwerking
          van uw informatie beperken.
        </li>
        <li>
          <Text strong>Gegevensoverdraagbaarheid:</Text> U kunt een kopie van uw
          informatie in een gestructureerd, veel gebruikt en machineleesbaar
          formaat opvragen.
        </li>
        <li>
          <Text strong>Bezwaar:</Text> U kunt bezwaar maken tegen onze
          verwerking van uw persoonlijke informatie onder bepaalde
          omstandigheden.
        </li>
      </ul>
      <Paragraph>
        Om deze rechten uit te oefenen, kunt u contact met ons opnemen via de
        contactgegevens aan het einde van dit beleid.
      </Paragraph>

      <Title level={2}>10. Cookies en vergelijkbare technologieën</Title>
      <Paragraph>
        Wij gebruiken cookies en vergelijkbare trackingtechnologieën om
        activiteit op ons platform bij te houden en bepaalde informatie vast te
        houden. Cookies zijn bestanden met een kleine hoeveelheid gegevens die
        een anonieme unieke identificatie kunnen bevatten.
      </Paragraph>
      <Paragraph>Wij gebruiken de volgende soorten cookies:</Paragraph>
      <ul>
        <li>
          <Text strong>Essentiële cookies:</Text> Noodzakelijk voor het correct
          functioneren van het platform
        </li>
        <li>
          <Text strong>Functionaliteitscookies:</Text> Onthouden uw voorkeuren
          en instellingen
        </li>
        <li>
          <Text strong>Analytische cookies:</Text> Helpen ons te begrijpen hoe u
          ons platform gebruikt
        </li>
        <li>
          <Text strong>Prestatiecookies:</Text> Helpen ons de prestaties van ons
          platform te optimaliseren
        </li>
      </ul>
      <Paragraph>
        U kunt uw browser opdracht geven om alle cookies te weigeren of aan te
        geven wanneer een cookie wordt verzonden. Als u echter geen cookies
        accepteert, werken sommige delen van ons platform mogelijk niet correct.
      </Paragraph>

      <Title level={2}>11. Privacy van kinderen</Title>
      <Paragraph>
        Onze diensten zijn niet gericht op personen jonger dan 18 jaar. Wij
        verzamelen niet bewust persoonlijke informatie van kinderen. Als u een
        ouder of voogd bent en denkt dat uw kind ons persoonlijke informatie
        heeft verstrekt, neem dan contact met ons op.
      </Paragraph>

      <Title level={2}>12. Wijzigingen in dit Privacybeleid</Title>
      <Paragraph>
        Wij kunnen ons Privacybeleid van tijd tot tijd bijwerken. Wij zullen u
        op de hoogte stellen van eventuele wijzigingen door het nieuwe
        Privacybeleid op deze pagina te plaatsen en de datum van "Laatst
        bijgewerkt" bij te werken. Wij raden u aan dit Privacybeleid regelmatig
        te controleren op eventuele wijzigingen.
      </Paragraph>

      <Title level={2}>13. Contact met ons opnemen</Title>
      <Paragraph>
        Als u vragen heeft over dit Privacybeleid, neem dan contact met ons op
        via:
      </Paragraph>
      <Paragraph>
        E-mail: info@efreightforwarding.com
        <br />
        Adres: Ir D S Tuijnmanweg 1A 4131PN, Vianen
      </Paragraph>
    </>
  );

  return (
    <Layout>
      <Content>
        <PageContainer>
          <StyledCard>
            <Row justify="space-between" align="middle">
              <Col>
                <Link to="/">
                  <Logo src="/logo_black.png" alt="E-Freight Forwarding Logo" />
                </Link>
              </Col>
              <Col>
                <LanguageSwitch>
                  <Text style={{ marginRight: "10px" }}>Language / Taal:</Text>
                  <Select
                    defaultValue={language}
                    style={{ width: 120 }}
                    onChange={changeLanguage}
                    options={[
                      { value: "en", label: "English" },
                      { value: "nl", label: "Nederlands" },
                    ]}
                  />
                </LanguageSwitch>
              </Col>
            </Row>

            {language === "en" ? privacyContentEn : privacyContentNl}
          </StyledCard>
        </PageContainer>
      </Content>
      <StyledFooter>
        <Text>
          © {new Date().getFullYear()} e-freight forwarding®. All rights
          reserved.
        </Text>
      </StyledFooter>
    </Layout>
  );
};
